<template>
  <div v-if="loading" class="skeleton-loader-wrapper">
    <SkeletonLoader :loadingText="loadingText" />
  </div>
  <div v-else>
    <MainHeader :title="'Create Account'">
      <template #left>
        <font-awesome-icon
          @click="router.push({ name: 'ConnectedAccounts' })"
          :icon="['fas', 'arrow-left-long']"
          class="pointer"
        />
      </template>
    </MainHeader>

    <div class="view aa-view">
      <div class="aa-header">
        <h1 class="aa-title">New Account</h1>
        <p class="aa-sub">
          Add a manual account to track balances and net worth.
        </p>
      </div>

      <form @submit.prevent="saveAccount">
        <fieldset>
          <div class="ud-section">
            <span class="section-label">Details</span>

            <div class="ud-field">
              <label class="ud-label"
                >Account Name <span class="ud-asterisk">*</span></label
              >
              <input
                type="text"
                class="ud-input"
                v-model="accountName"
                placeholder="e.g. Chase Checking"
                required
              />
            </div>
            <div class="ud-field mt">
              <label class="ud-label"
                >Institution <span class="ud-asterisk">*</span></label
              >
              <input
                type="text"
                class="ud-input"
                v-model="institutionName"
                placeholder="e.g. Chase Bank"
                required
              />
            </div>
            <div class="ud-field mt">
              <label class="ud-label" for="accountType"
                >Account Type <span class="ud-asterisk">*</span></label
              >
              <select
                v-model="accountType"
                class="ud-input ud-select form-select"
                id="accountType"
                required
              >
                <option value="" disabled selected>Select account type</option>
                <option
                  v-for="(type, key) in accountTypesList"
                  :key="key"
                  :value="type"
                  :selected="type == store.selectedAccount.accountType"
                >
                  {{ type }}
                </option>
              </select>
            </div>
          </div>

          <div class="ud-section">
            <span class="section-label">Balances</span>

            <div class="ud-field">
              <label class="ud-label"
                >Available Balance <span class="ud-asterisk">*</span></label
              >
              <input
                type="number"
                class="ud-input"
                step="0.01"
                min="0"
                v-model="availableBalance"
                placeholder="0.00"
                required
              />
            </div>
            <div class="ud-field mt">
              <label class="ud-label"
                >Current Balance <span class="ud-asterisk">*</span></label
              >
              <input
                type="number"
                class="ud-input"
                step="0.01"
                min="0"
                v-model="currentBalance"
                placeholder="0.00"
                required
              />
            </div>
          </div>

          <div class="ud-section">
            <span class="section-label">Appearance</span>

            <div class="ud-field">
              <label class="ud-label">Account Icon</label>
              <IconSearch
                button-text="Choose Icon"
                styleClass="form-dropdown"
                @optionSelected="iconSelected($event)"
              />
            </div>
            <div class="ud-field mt">
              <label class="ud-label">Account Color</label>
              <ColorSelection
                button-text="Choose Color"
                styleClass="form-dropdown"
                @optionSelected="iconColorSelected($event)"
              />
            </div>
          </div>

          <button class="ud-submit" type="submit">Create Account</button>
        </fieldset>
      </form>
    </div>
  </div>
</template>

<script>
import { useMyStore } from '../store';
import { useRouter } from 'vue-router';
import { ref, reactive, onBeforeUnmount, onMounted } from 'vue';
import MainHeader from '../components/View-Header.vue';
import IconSearch from '../components/Icon-Selector.vue';
import ColorSelection from '../components/Color-Selection.vue';
import { Account_Types } from '../utils/constants.js';
import SkeletonLoader from '../components/SkeletonLoader.vue';

export default {
  components: { MainHeader, IconSearch, ColorSelection, SkeletonLoader },
  setup() {
    const store = useMyStore();
    const router = useRouter();
    const searchTerm = ref('');
    const searchResults = reactive({});
    const accountName = ref('');
    const institutionName = ref('');
    const accountBalance = ref('');
    const availableBalance = ref('');
    const currentBalance = ref(0);
    let accountType = ref('');
    const accountIcon = ref('');
    const accountColor = ref('');
    const accountTypesList = ref(Account_Types);
    let loading = ref(true);
    let loadingText = ref('Loading...');

    const saveAccount = async () => {
      loadingText.value = 'Saving Account...';
      loading.value = true;
      try {
        const accountData = {
          AvailableBalance: parseInt(availableBalance.value),
          CurrentBalance: parseInt(currentBalance.value),
          DisplayName: accountName.value,
          AccountType: accountType.value,
          InstitutionName: institutionName.value,
          AccountIcon: accountIcon.value,
          AccountIconColor: accountColor.value,
        };
        await store.saveCustomAccount(accountData);
        await store.getPlaidAccountsList();
        accountName.value = '';
        institutionName.value = '';
        availableBalance.value = '';
        currentBalance.value = 0;
        accountType.value = '';
        accountIcon.value = '';
        accountColor.value = '';
        loading.value = false;
        router.push({ name: 'ConnectedAccounts' });
      } catch (error) {
        loading.value = false;
        console.error('An error occurred:', error);
      }
    };

    // const saveAccount = () => {
    //   loadingText.value = 'Saving Account...';
    //   loading.value = true;
    //   try {
    //     const accountData = {
    //       AvailableBalance: parseInt(availableBalance.value),
    //       CurrentBalance: parseInt(currentBalance.value),
    //       DisplayName: accountName.value,
    //       AccountType: accountType.value,
    //       InstitutionName: institutionName.value,
    //       AccountIcon: accountIcon.value,
    //       AccountIconColor: accountColor.value,
    //     };
    //     store.saveCustomAccount(accountData).then(() => {
    //       loading.value = false;
    //       router.push({ name: 'ConnectedAccounts' });
    //     });
    //   } catch (error) {
    //     loading.value = false;
    //     console.error('An error occurred:', error);
    //   }
    // };

    const iconSelected = (icon) => {
      accountIcon.value = icon;
    };
    const iconColorSelected = (color) => {
      accountColor.value = color;
    };

    onMounted(() => {
      setTimeout(() => {
        const mainElement = document.querySelector('.main');
        if (mainElement) mainElement.scrollTo(0, 0);
      }, 100);
      loading.value = false;
    });

    onBeforeUnmount(() => {
      store.updateAddSubcategoryToEditCategory(false);
    });

    return {
      store,
      router,
      searchTerm,
      searchResults,
      ColorSelection,
      accountName,
      saveAccount,
      institutionName,
      accountBalance,
      accountType,
      iconSelected,
      accountIcon,
      accountColor,
      iconColorSelected,
      accountTypesList,
      availableBalance,
      currentBalance,
      loading,
      loadingText,
    };
  },
};
</script>

<style scoped>
.aa-view {
  max-width: 480px;
  margin: 0 auto;
}

.aa-header {
  text-align: center;
  padding: 1.75rem 0 0.25rem;
  margin-bottom: 1.5rem;
}
.aa-title {
  font-size: 1.3rem;
  font-weight: 700;
  color: #004751;
  margin: 0 0 0.3rem;
  letter-spacing: -0.02em;
}
.dark-theme .aa-title {
  color: #5dcaa5;
}
.aa-sub {
  font-size: 0.82rem;
  color: #888;
  margin: 0;
}
</style>

<!-- <template>
  <div v-if="loading" class="skeleton-loader-wrapper">
    <SkeletonLoader :loadingText="loadingText" />
  </div>
  <div v-else>
    <div>
      <MainHeader :title="'Create Account'">
        <template #left>
          <font-awesome-icon
            @click="router.push({ name: 'ConnectedAccounts' })"
            :icon="['fas', 'arrow-left-long']"
            class="pointer"
          />
        </template>
      </MainHeader>
    </div>
    <div class="view">
      <div class="add-account-form">
        <div class="text-center pb-4">
          <font-awesome-icon
            :icon="['fas', 'folder-plus']"
            class="view-icon add-cat-icon"
          />
        </div>
        <form @submit.prevent="saveAccount">
          <fieldset>
            <div class="mt-1">
              <label class="small-grey-text">Account Name *</label>
              <input type="text" class="input" v-model="accountName" required />
            </div>
            <div class="mt-1">
              <label class="small-grey-text">Institution *</label>
              <input
                type="text"
                class="input"
                v-model="institutionName"
                required
              />
            </div>
            <div class="mt-1">
              <label class="small-grey-text">Available Balance *</label>
              <input
                type="number"
                class="input"
                step="0.01"
                min="0"
                v-model="availableBalance"
                required
              />
            </div>
            <div class="mt-1">
              <label class="small-grey-text">Current Balance *</label>
              <input
                type="number"
                class="input"
                step="0.01"
                min="0"
                v-model="currentBalance"
                required
              />
            </div>
            <div class="mt-1">
              <label for="accountType" class="small-grey-text"
                >Account Type *</label
              >
              <select
                v-model="accountType"
                class="input form-select white-form-select"
                required
              >
                <option value="" disabled selected>Select Account Type</option>
                <option
                  v-for="(type, key) in accountTypesList"
                  :key="key"
                  :value="type"
                  :selected="type == store.selectedAccount.accountType"
                >
                  {{ type }}
                </option>
              </select>
            </div>
            <div class="mt-1">
              <label class="small-grey-text">Account Icon</label>
              <IconSearch
                button-text="Choose Icon"
                styleClass="form-dropdown"
                @optionSelected="iconSelected($event)"
              />
            </div>
            <div class="mt-1">
              <label class="small-grey-text">Account Color</label>
              <ColorSelection
                button-text="Choose Color"
                styleClass="form-dropdown"
                @optionSelected="iconColorSelected($event)"
              ></ColorSelection>
            </div>
            <div class="mt-8">
              <button class="blue-button w-100" type="submit">
                Create Account
              </button>
            </div>
          </fieldset>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { useMyStore } from '../store';
import { useRouter } from 'vue-router';
import { ref, reactive, onBeforeUnmount, onMounted } from 'vue';

import MainHeader from '../components/View-Header.vue';
import IconSearch from '../components/Icon-Selector.vue';
import ColorSelection from '../components/Color-Selection.vue';
import { Account_Types } from '../utils/constants.js';
import SkeletonLoader from '../components/SkeletonLoader.vue';

export default {
  components: { MainHeader, IconSearch, ColorSelection, SkeletonLoader },
  setup() {
    const store = useMyStore();
    const router = useRouter();
    const searchTerm = ref('');
    const searchResults = reactive({});
    const accountName = ref('');
    const institutionName = ref('');
    const accountBalance = ref('');
    const availableBalance = ref('');
    const currentBalance = ref(0);
    let accountType = ref('');
    const accountIcon = ref('');
    const accountColor = ref('');
    const accountTypesList = ref(Account_Types);
    let loading = ref(true);
    let loadingText = ref('Loading...');

    const saveAccount = () => {
      loadingText.value = 'Saving Account...';
      loading.value = true;

      try {
        const availableBalanceValue = availableBalance.value;
        const currentBalanceValue = currentBalance.value;

        const accountData = {
          AvailableBalance: parseInt(availableBalanceValue),
          CurrentBalance: parseInt(currentBalanceValue),
          DisplayName: accountName.value,
          AccountType: accountType.value,
          InstitutionName: institutionName.value,
          AccountIcon: accountIcon.value,
          AccountIconColor: accountColor.value,
        };
        store.saveCustomAccount(accountData).then(() => {
          loading.value = false;
          router.push({ name: 'ConnectedAccounts' });
        });
      } catch (error) {
        loading.value = false;
        console.error('An error occurred:', error);
      }
    };

    const iconSelected = (icon) => {
      accountIcon.value = icon;
    };

    const iconColorSelected = (color) => {
      accountColor.value = color;
    };

    onMounted(() => {
      setTimeout(() => {
        const mainElement = document.querySelector('.main');
        if (mainElement) {
          mainElement.scrollTo(0, 0);
        }
      }, 100);

      loading.value = false;
    });

    onBeforeUnmount(() => {
      store.updateAddSubcategoryToEditCategory(false);
    });

    return {
      store,
      router,
      searchTerm,
      searchResults,
      ColorSelection,
      accountName,
      saveAccount,
      institutionName,
      accountBalance,
      accountType,
      iconSelected,
      accountIcon,
      accountColor,
      iconColorSelected,
      accountTypesList,
      availableBalance,
      currentBalance,
      loading,
      loadingText,
    };
  },
};
</script>

<style scoped>
.add-cat-icon {
  color: #e8f44a;
}
</style> -->
